import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";


function getPageWindow(current, total, maxButtons) {
  if (total <= maxButtons) return Array.from({ length: total }, (_, i) => i + 1);

  const half = Math.floor(maxButtons / 2);
  let start = Math.max(1, current - half);
  let end = start + maxButtons - 1;

  if (end > total) {
    end = total;
    start = end - maxButtons + 1;
  }

  return Array.from({ length: maxButtons }, (_, i) => start + i);
}

export default function Pagination({
  page,
  totalPages,
  onChange,
  maxButtons = 5,
}) {
  const { t } = useTranslation();

  const pages = useMemo(
    () => getPageWindow(page, totalPages, maxButtons),
    [page, totalPages, maxButtons]
  );

  const isFirst = page === 1;
  const isLast = page === totalPages;

  return (
    <div className="mt-8 flex items-center justify-center gap-3">
      <button
        type="button"
        onClick={() => onChange(1)}
        disabled={isFirst}
        aria-label={t("PAGINATION.FIRST")}
        className="
          w-10 h-10 rounded-full border border-gray-300
          flex items-center justify-center text-lg leading-none
          hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
        "
      >
        «
      </button>

      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={isFirst}
        aria-label={t("PAGINATION.PREVIOUS")}
        className="
          w-10 h-10 rounded-full bg-gray-900 text-white
          flex items-center justify-center text-lg leading-none
          hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed
        "
      >
        ‹
      </button>

      <div className="flex items-center gap-2">
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            aria-current={p === page ? "page" : undefined}
            className={
              p === page
                ? "w-10 h-10 rounded-full border border-transparent text-primary font-semibold text-sm"
                : "w-10 h-10 rounded-full border border-gray-300 text-gray-700 text-sm hover:text-primary hover:bg-gray-50"
            }
          >
            {p}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={isLast}
        aria-label={t("PAGINATION.NEXT")}
        className="
          w-10 h-10 rounded-full bg-gray-900 text-white
          flex items-center justify-center text-lg leading-none
          hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed
        "
      >
        ›
      </button>

      <button
        type="button"
        onClick={() => onChange(totalPages)}
        disabled={isLast}
        aria-label={t("PAGINATION.LAST")}
        className="
          w-10 h-10 rounded-full border border-gray-300
          flex items-center justify-center text-lg leading-none
          hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
        "
      >
        »
      </button>
    </div>
  );
}